/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {BroadcastMessage} from './broadcast';
import type {
  DiffAndTokenizeParams,
  DiffAndTokenizeResponse,
  LineRangeParams,
  LineRangeResponse,
  LineToPositionParams,
  Message,
  Response,
  Result,
} from './diffServiceWorker';
import type {LineToPosition} from './lineToPosition';
import type {SupportedPrimerColorMode} from './themeState';

import {
  AVAILABILITY_METHOD,
  createDiffServiceBroadcastChannel,
  createWorkerName,
  parseWorkerIndex,
} from './broadcast';
import {atom as jotaiAtom} from 'jotai';
import {atomFamily} from 'jotai-family';
import {unwrap} from 'shared/utils';
import rejectAfterTimeout from 'shared/rejectAfterTimeout';

/** Timeout for worker responses. If a worker doesn't respond within this time,
 *  the request is rejected rather than hanging forever. */
const WORKER_RESPONSE_TIMEOUT_MS = 30 * 1000;

/**
 * Maximum number of SharedWorkers in the pool. Each worker runs in its own
 * thread, so N workers = N parallel diff computations. Capped to avoid
 * excessive memory use (each worker loads its own grammar store, theme, etc.).
 */
const MAX_WORKERS = 5;//Math.min(navigator?.hardwareConcurrency ?? 4, 4);

/**
 * Client that is paired with an instance of `diffServiceWorker`. Takes
 * responsibility for pairing requests and responses to the Web Worker, making
 * the result available as a Promise to the caller.
 */
class DiffServiceClient {
  private worker: SharedWorker;
  private workerName: string;
  private pendingRequests: Map<number, (result: Result) => void> = new Map();

  constructor(workerName: string) {
    this.workerName = workerName;
    this.worker = new SharedWorker(new URL('./diffServiceWorker.ts', import.meta.url), {
      name: workerName,
    });
    this.worker.port.onmessage = event => this.onmessage(event);
    // eslint-disable-next-line no-console
    this.worker.port.onmessageerror = event => console.error(event);
    // eslint-disable-next-line no-console
    this.worker.onerror = event => console.error('SharedWorker error:', event);
    // Explicitly start the port to ensure messages flow
    this.worker.port.start();
  }

  private onmessage({data}: {data: Response & {log?: string}}) {
    // Handle relay log messages from the worker
    if (data.log != null) {
      return;
    }

    const {id, ok, err, workerMs} = data;
    const handler = this.pendingRequests.get(id);
    if (handler == null) {
      // eslint-disable-next-line no-console
      console.error(`no handler found for ${id}: multiple responses sent?`);
      return;
    }

    this.pendingRequests.delete(id);
    handler({ok, err, workerMs});
  }

  private once(id: number, onresponse: (response: Result) => void) {
    this.pendingRequests.set(id, onresponse);
  }

  sendMessage(message: Message): Promise<unknown> {
    const {id} = message;
    const startTime = performance.now();

    const promise = new Promise((resolve, reject) => {
      this.once(id, (result: Result) => {
        const roundtrip = Math.round(performance.now() - startTime);
        const {ok, err, workerMs} = result;
        const overhead = workerMs != null ? roundtrip - workerMs : null;

        if (err != null) {
          // eslint-disable-next-line no-console
          console.log(`[Worker:${this.workerName}] id=${id} method=${message.method} ERROR roundtrip=${roundtrip}ms worker=${workerMs ?? '?'}ms overhead=${overhead ?? '?'}ms`);
          reject(err);
        } else {
          // eslint-disable-next-line no-console
          console.log(`[Worker:${this.workerName}] id=${id} method=${message.method} OK roundtrip=${roundtrip}ms worker=${workerMs ?? '?'}ms overhead=${overhead ?? '?'}ms`);
          resolve(ok);
        }
      });
    });
    this.worker.port.postMessage(message);
    return rejectAfterTimeout(
      promise,
      WORKER_RESPONSE_TIMEOUT_MS,
      `Worker ${this.workerName} did not respond to message id=${id} method=${message.method} within ${WORKER_RESPONSE_TIMEOUT_MS}ms`,
    );
  }
}

class WorkerPool {
  /** Used to get updates about the availability of SharedWorkers. */
  private broadcast: BroadcastChannel;

  /**
   * Workers are created on-demand up to MAX_WORKERS. Index N is created when
   * all workers 0..N-1 are busy, giving true parallel processing across threads.
   */
  private workers: Array<undefined | {client: DiffServiceClient; available: boolean}> = [];

  /**
   * Messages that are waiting for a SharedWorker to become available in order
   * to be sent. The response from sendMessage() should be passed to resolve or
   * reject, as appropriate.
   */
  private pendingMessages: {
    message: Message;
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
  }[] = [];

  /**
   * Used by this.nextID() to ensure each message sent from the pool gets a
   * unique ID so it can be paired with the response.
   */
  private requestID = 0;

  constructor() {
    this.broadcast = createDiffServiceBroadcastChannel();
    this.broadcast.onmessage = (event: MessageEvent) => this.onBroadcastMessageReceived(event);
  }

  private onBroadcastMessageReceived({data}: MessageEvent) {
    const message = data as BroadcastMessage;
    if (message.method !== AVAILABILITY_METHOD) {
      return;
    }

    const {workerName, available} = message;

    const index = parseWorkerIndex(workerName);
    if (index == null) {
      // eslint-disable-next-line no-console
      console.error(`could not parse worker index: ${workerName}`);
      return;
    }

    // Only handle broadcasts from workers we own. Workers from other
    // sessions (different SESSION_ID in the name) should be ignored.
    const worker = this.workers[index];
    if (worker === undefined) {
      // We don't create clients for broadcast-discovered workers anymore.
      // Workers are only created explicitly by sendMessage(). This avoids
      // adopting stale workers from other sessions that share the same
      // BroadcastChannel.
      return;
    }
    worker.available = available;

    if (available && this.pendingMessages.length > 0) {
      this.trySendingPendingMessage();
    }
  }

  sendMessage(message: Message): Promise<unknown> {
    // Route to an available worker if one exists.
    const found = this.findAvailableWorkerEntry();
    if (found != null) {
      // Optimistically mark busy so the next sendMessage() won't pick the
      // same worker before the broadcast arrives from the worker thread.
      found.entry.available = false;
      // eslint-disable-next-line no-console
      console.log(`[WorkerPool] id=${message.id} method=${message.method} → worker-${found.index}`);
      return found.entry.client.sendMessage(message);
    }

    // All existing workers are busy. If we haven't hit the cap, spin up a new
    // worker — each SharedWorker runs in its own thread, giving true parallelism.
    const workerCount = this.workers.filter(w => w !== undefined).length;
    if (workerCount < MAX_WORKERS) {
      const workerName = createWorkerName(workerCount);
      const newClient = new DiffServiceClient(workerName);
      // Mark as unavailable immediately — we're about to send it work.
      this.workers[workerCount] = {client: newClient, available: false};
      // eslint-disable-next-line no-console
      console.log(`[WorkerPool] id=${message.id} method=${message.method} → worker-${workerCount} (new)`);
      return newClient.sendMessage(message);
    }

    // At capacity — queue until a worker becomes available.
    // eslint-disable-next-line no-console
    console.log(`[WorkerPool] id=${message.id} method=${message.method} → QUEUED (${this.pendingMessages.length + 1} pending)`);
    return this.enqueueMessage(message);
  }

  private enqueueMessage(message: Message): Promise<unknown> {
    let resolve: ((value: unknown) => void) | null = null;
    let reject: ((error: Error) => void) | null = null;
    const promise = new Promise((_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    });

    this.pendingMessages.push({
      message,
      resolve: unwrap<(value: unknown) => void>(resolve),
      reject: unwrap<(error: Error) => void>(reject),
    });

    // Apply a timeout to the queued promise. If no worker becomes available
    // within the timeout, reject rather than waiting forever.
    return rejectAfterTimeout(
      promise,
      WORKER_RESPONSE_TIMEOUT_MS,
      `No worker became available for message id=${message.id} method=${message.method} within ${WORKER_RESPONSE_TIMEOUT_MS}ms`,
    );
  }

  private trySendingPendingMessage(): void {
    const found = this.findAvailableWorkerEntry();
    if (found == null) {
      return;
    }

    const pendingMessage = this.pendingMessages.shift();
    if (pendingMessage === undefined) {
      return;
    }

    found.entry.available = false;
    const {message, resolve, reject} = pendingMessage;
    // eslint-disable-next-line no-console
    console.log(`[WorkerPool] id=${message.id} method=${message.method} → worker-${found.index} (dequeued, ${this.pendingMessages.length} remaining)`);
    found.entry.client.sendMessage(message).then(resolve, reject);
  }

  private findAvailableWorkerEntry(): {index: number; entry: {client: DiffServiceClient; available: boolean}} | null {
    for (let i = 0; i < this.workers.length; i++) {
      const worker = this.workers[i];
      if (worker !== undefined && worker.available) {
        return {index: i, entry: worker};
      }
    }
    return null;
  }

  nextID(): number {
    return ++this.requestID;
  }
}

// =============================================================================
// Jotai versions of the diff service selectors
// =============================================================================

// Singleton WorkerPool instance shared across all Jotai atoms
let workerPoolInstance: WorkerPool | null = null;
function getWorkerPool(): WorkerPool {
  if (workerPoolInstance == null) {
    workerPoolInstance = new WorkerPool();
  }
  return workerPoolInstance;
}

/**
 * Jotai atom for the diff service worker pool.
 * Uses a singleton to share the pool across all atoms.
 */
export const diffServiceClientAtom = jotaiAtom<WorkerPool>(getWorkerPool());

/**
 * Jotai atomFamily for diffAndTokenize requests.
 */
export const diffAndTokenizeAtom = atomFamily(
  (params: DiffAndTokenizeParams) =>
    jotaiAtom(async () => {

      const worker = getWorkerPool();
      const message: Message = {
        id: worker.nextID(),
        method: 'diffAndTokenize',
        params,
      };
      const result = (await worker.sendMessage(message)) as DiffAndTokenizeResponse;

      return result;
    }),
  (a, b) =>
    a.path === b.path &&
    a.before === b.before &&
    a.after === b.after &&
    a.scopeName === b.scopeName &&
    a.colorMode === b.colorMode &&
    (a.ignoreWhitespace ?? false) === (b.ignoreWhitespace ?? false),
);

/**
 * Jotai atomFamily for colorMap requests.
 */
export const colorMapAtom = atomFamily(
  (colorMode: SupportedPrimerColorMode) =>
    jotaiAtom(async () => {
      const worker = getWorkerPool();
      const message: Message = {
        id: worker.nextID(),
        method: 'colorMap',
        params: {colorMode},
      };
      return (await worker.sendMessage(message)) as string[];
    }),
  (a, b) => a === b,
);

/**
 * Jotai atomFamily for lineRange requests.
 */
export const lineRangeAtom = atomFamily(
  (params: LineRangeParams) =>
    jotaiAtom(async () => {
      const worker = getWorkerPool();
      const message: Message = {
        id: worker.nextID(),
        method: 'lineRange',
        params,
      };
      const response = (await worker.sendMessage(message)) as LineRangeResponse;
      const {unsplitLines, notFound, isBinary} = response;
      if (unsplitLines != null) {
        return unsplitLines.split('\n');
      } else if (notFound) {
        // eslint-disable-next-line no-console
        console.error(`blob ${params.oid} not found for lineRange`);
      } else if (isBinary) {
        // eslint-disable-next-line no-console
        console.error(`blob ${params.oid} is binary, no lineRange`);
      }
      return [];
    }),
  (a, b) => a.oid === b.oid && a.start === b.start && a.numLines === b.numLines,
);

/**
 * Jotai atomFamily for lineToPosition requests.
 */
export const lineToPositionAtom = atomFamily(
  (params: LineToPositionParams) =>
    jotaiAtom(async () => {
      const worker = getWorkerPool();
      const message: Message = {
        id: worker.nextID(),
        method: 'lineToPosition',
        params,
      };
      return (await worker.sendMessage(message)) as LineToPosition;
    }),
  (a, b) => a.oldOID === b.oldOID && a.newOID === b.newOID,
);
